/*
 * Lemonade Training Oy copy rights all right reserved.
 * Platform-level access: the gym licence (Users.boxLicense), its audit trail
 * (PlatformAccessAudit) and the setBoxAccessByEmail callable contract.
 *
 * Every const here must survive to dist/ as real JavaScript: functions/ is
 * plain JS and validates against these lists at runtime.
 */

import type { Timestamp } from "firebase/firestore";

/* -------------- [ Users/{uid}.boxLicense ] ------------------ */
export const BOX_LICENSE_SOURCES = ["admin_portal", "purchase"] as const;
export type BoxLicenseSource = (typeof BOX_LICENSE_SOURCES)[number];

/**
 * Permission to create and run a gym, granted before any gym exists. Absent
 * means unlicensed; it is never written as null. Cloud Functions are its only
 * writer (rules deny the self-write). Claim derivation mints `box: true` from
 * it whenever the holder has no active gym, so the licence is dormant while
 * they belong to someone else's gym and returns when they leave it.
 */
export type BoxLicense = {
  grantedAt: Timestamp | Date;
  /** Actor uid, or "system" for the purchase path. */
  grantedBy: string;
  source: BoxLicenseSource;
};

/* -------------- [ PlatformAccessAudit/{auto} ] ------------------ */
export const PLATFORM_ACCESS_AUDIT_ACTIONS = ["grant_box_license"] as const;
export type PlatformAccessAuditAction =
  (typeof PLATFORM_ACCESS_AUDIT_ACTIONS)[number];

/** uids only — never a display name or email. Kept after account deletion. */
export type PlatformAccessAuditEntry = {
  id?: string;
  action: PlatformAccessAuditAction;
  targetUID: string;
  /** Actor uid, or "system". */
  actorUID: string;
  source: BoxLicenseSource;
  createdAt: Timestamp | Date;
};

/* -------------- [ setBoxAccessByEmail callable ] ------------------ */
export type AssignBoxLicenseRequest = { email: string };

export const BOX_LICENSE_OUTCOMES = [
  "granted",
  "already_licensed",
  "platform_admin",
] as const;
export type BoxLicenseOutcome = (typeof BOX_LICENSE_OUTCOMES)[number];

/**
 * Where the target stands at a gym when the licence lands. A member or coach
 * of another gym is refused with failed-precondition, so only these reach a
 * response.
 */
export const BOX_LICENSE_GYM_BINDINGS = ["none", "admin"] as const;
export type BoxLicenseGymBinding = (typeof BOX_LICENSE_GYM_BINDINGS)[number];

export type AssignBoxLicenseResponse = {
  outcome: BoxLicenseOutcome;
  gymBinding: BoxLicenseGymBinding;
  /** The licence is stored but the token mint failed; Users.claimsStale is set and a retry re-mints. */
  claimsPending: boolean;
  /** The PlatformAccessAudit id, or null when nothing was written. */
  auditID: string | null;
};
